import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";

import ModalPortal from "modules/patient/sections/common/ModalPortal";
import MOBILE from "../mobileConfig";
import { lockScroll, unlockScroll } from "../scrollLock";

/**
 * The mobile bottom sheet — what the desktop 340px editor rail becomes.
 *
 * TWO THINGS ABOUT THIS ARE LOAD-BEARING.
 *
 * 1. CHILDREN ARE ALWAYS MOUNTED, open or shut. The section editors are what
 *    listen for their own CC_V2_ADD / DX_V2_ADD / ... events, so unmounting one
 *    while the sheet is closed would leave nothing to hear the button that is
 *    supposed to reopen it. PatientShell has the same constraint and solves it
 *    the same way — it hides `.ph-editor` with `display: none` rather than
 *    dropping it (PatientShell.js:39-43). The closed sheet costs nothing to
 *    draw: SheetStack returns null with an empty stack (SheetStack.js:13), so
 *    there is no content inside it anyway.
 *
 * 2. HOW TALL IT RISES IS A CHOICE, per `clearTopBar`. On the patient record
 *    the sheet stops short of row 1 rather than covering it — the patient whose
 *    record is being edited stays on screen, at `--zym-row1-h`, published live
 *    by useCssVarHeight. Elsewhere it takes the height it needs.
 *
 *    THE BACKDROP ALWAYS COVERS THE WHOLE VIEWPORT, including row 1. Leaving a
 *    strip undimmed reads as a strip that is still live, and a half-dimmed
 *    screen just looks like the overlay failed to paint. What row 1 gets is
 *    room, not an exemption.
 *
 * NO SWIPE TO DISMISS, on purpose. These sheets hold long forms that scroll,
 * and a downward drag inside one has to mean "scroll" — a sheet that could also
 * be thrown away by dragging down would eat the first scroll of every form.
 * Dismissal is the X (always on screen, never scrolls away) and the backdrop.
 */
const BottomSheet = ({
  open,
  title,
  onClose,
  className,
  clearTopBar,
  nested,
  overDialog,
  footer,
  children,
}) => {
  const panelRef = useRef(null);
  const lastFocused = useRef(null);
  /* onClose through a ref, so the effect below runs on OPEN and CLOSE only.
     Hosts pass it inline — a new function every render — and with it in the
     deps every re-render inside an open sheet ran the cleanup as if the sheet
     had closed: it re-focused the element focused at open. In the lab search
     that is the search box, so each tick blurred it (keyboard down) and the
     re-render focused it again (keyboard straight back up). */
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  /* Scroll lock + focus restore, after Dialog.js — though the lock is the
     layer's shared, counted one (scrollLock.js) rather than saved per sheet as
     Dialog saves it. Not reusing Dialog itself: it is a centred desktop dialog
     whose whole geometry is wrong here, and it would unmount our
     always-mounted children. */
  useEffect(() => {
    if (!open) return undefined;

    lastFocused.current = document.activeElement;
    lockScroll();

    const onKey = (e) => {
      if (e.key === "Escape") onCloseRef.current();
    };
    document.addEventListener("keydown", onKey);

    return () => {
      document.removeEventListener("keydown", onKey);
      unlockScroll();
      if (lastFocused.current && lastFocused.current.focus) {
        lastFocused.current.focus();
      }
    };
  }, [open]);

  const onBackdrop = (e) => {
    if (!MOBILE.sheet.dismissOnBackdrop) return;
    // mousedown-on-self, so a drag that STARTED inside the panel and ended on
    // the backdrop (selecting text, say) does not read as a dismiss.
    if (e.target !== e.currentTarget) return;
    onClose();
  };

  return (
    <ModalPortal>
      <div
        className={`zym-sheet-layer${open ? " is-open" : ""}${
          clearTopBar ? " is-below-topbar" : ""
        }${nested ? " is-nested" : ""}${overDialog ? " is-over-dialog" : ""}`}
      >
        {/* aria-hidden while shut: the editor subtree stays in the DOM to keep
            its listeners alive, and a screen reader must not wander into a form
            that is not on screen. */}
        <div
          className="zym-sheet-backdrop"
          onMouseDown={onBackdrop}
          aria-hidden="true"
        />
        <div
          ref={panelRef}
          className={`zym-sheet${className ? ` ${className}` : ""}`}
          role="dialog"
          aria-modal={open ? "true" : undefined}
          aria-label={title}
          aria-hidden={open ? undefined : "true"}
        >
          <div className="zym-sheet-head">
            <span className="zym-sheet-title">{title}</span>
            <button
              type="button"
              className="zym-sheet-close"
              onClick={onClose}
              aria-label="Close"
            >
              <svg viewBox="0 0 14 14" aria-hidden="true" focusable="false">
                <path
                  d="M1 1l12 12M13 1L1 13"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          </div>
          <div className="zym-sheet-body">{children}</div>

          {/* OUTSIDE the scrolling body, deliberately. A sticky footer inside
              a scroller is pulled UP out of its flow position whenever the
              content overflows, and it then floats over whatever precedes it —
              which is how the appointment sheet's Create Rx row came to sit on
              top of Reschedule. As a sibling it simply cannot overlap: the body
              takes the space that is left and scrolls within it. */}
          {footer ? <div className="zym-sheet-foot">{footer}</div> : null}
        </div>
      </div>
    </ModalPortal>
  );
};

BottomSheet.propTypes = {
  open: PropTypes.bool,
  /** Accessible name, and the text in the sheet's own header. */
  title: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  className: PropTypes.string,
  /** Stop the sheet short of row 1 instead of covering it. */
  clearTopBar: PropTypes.bool,
  /** Opens ON TOP of another sheet — raises the layer above the base one. */
  nested: PropTypes.bool,
  /** Opens ON TOP of a desktop dialog (z-index 1500) — raises the layer above it. */
  overDialog: PropTypes.bool,
  /** Pinned below the scrolling body — never overlaps it. */
  footer: PropTypes.node,
  children: PropTypes.node,
};

export default BottomSheet;
